// Chat prefill for "Try it" links on TQA cards. Service and connect
// pages append chat_prefill_* params to the /create URL; this module parses them
// and builds the prompt that seeds the chat intro input.

import truncate from "lodash/truncate"

export type TqaPrefillType = "trigger" | "query" | "action"

export interface TqaPrefill {
  type: TqaPrefillType
  tqaName: string
  serviceName: string
  activeTab: string | null
  serviceModuleName: string | null
  prompt: string
}

// The name caps keep the whole prompt (template ≈ 60 chars + two names + type)
// well under the intro textarea's maxLength=500, which would otherwise cut it.
const MAX_NAME_LENGTH = 120

const TEMPLATE_PARAM_BY_TYPE: Record<TqaPrefillType, string> = {
  trigger: "template_trigger",
  query: "template_queries",
  action: "template_actions",
}

const truncateName = (value: string) => truncate(value, { length: MAX_NAME_LENGTH, omission: "…" })

const isTqaPrefillType = (value: string | null): value is TqaPrefillType =>
  value === "trigger" || value === "query" || value === "action"

export const buildPrefillPrompt = ({
  type,
  tqaName,
  serviceName,
}: Pick<TqaPrefill, "type" | "tqaName" | "serviceName">): string =>
  `I want to build an Applet that uses the "${truncateName(tqaName)}" ${type} from the ${truncateName(serviceName)} service.`

export const parseChatPrefillFromParams = (urlParams: URLSearchParams): TqaPrefill | null => {
  const type = urlParams.get("chat_prefill_type")
  if (!isTqaPrefillType(type)) return null

  const tqaName = urlParams.get("chat_prefill_tqa")?.trim()
  const serviceName = urlParams.get("chat_prefill_service")?.trim()
  if (!tqaName || !serviceName) return null

  // Try it links carry a single module in the matching template param,
  // e.g. template_trigger=weather.temperature_drops -> "weather".
  const templateModule = urlParams.get(TEMPLATE_PARAM_BY_TYPE[type])
  const serviceModuleName = templateModule?.split(".")[0] || null

  return {
    type,
    tqaName,
    serviceName,
    activeTab: urlParams.get("chat_prefill_tab"),
    serviceModuleName,
    prompt: buildPrefillPrompt({ type, tqaName, serviceName }),
  }
}
